<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { routes } from '@app/router/routes'
import { config } from '@core/config/config'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Image from '@uiKit/elements/Image.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import logo from '@assets/images/logo.png'
import slicealText from '@assets/images/sliceal-text.png'
import varaLogo from '@assets/images/varald.png'

// Left navigation rail for the authenticated shell. Sections that don't have a
// module yet render as disabled rows (faithful to the design, honest about what
// ships) so no dead routes are created. Emits `navigate` so the mobile drawer
// host can close itself after a tap.
defineEmits<{ navigate: [] }>()

// Build-time value from package.json via the `__APP_VERSION__` vite define —
// same identity Sentry / OTel / logs report, so the footer can never drift from
// the deployed build.
const appVersion = config.app.version

interface NavItem {
  label: string
  icon: PiIconName
  to?: string
  enabled: boolean
}

const primaryItems: NavItem[] = [
  { label: 'Dashboard', icon: 'table', to: routes.DASHBOARD, enabled: true },
  { label: 'Sell Request', icon: 'tag', to: routes.LISTINGS, enabled: true },
  { label: 'Properties', icon: 'building', to: routes.PROPERTIES, enabled: true },
  { label: 'Marketplace', icon: 'shopping-bag', enabled: false },
]

const supportItems: NavItem[] = [
  { label: 'Help & Support', icon: 'question-circle', enabled: false },
  { label: 'Settings', icon: 'cog', enabled: false },
]
</script>

<template>
  <Box is="aside" direction="col" class="sidebar">
    <RouterLink :to="routes.DASHBOARD" class="sidebar__brand" aria-label="Sliceal home">
      <Image :src="logo" alt="" width="30" height="30" class="sidebar__brand-icon" />
      <Image :src="slicealText" alt="Sliceal" class="sidebar__brand-wordmark" />
    </RouterLink>

    <Box is="nav" direction="col" gap="1" class="sidebar__nav" aria-label="Primary">
      <template v-for="item in primaryItems" :key="item.label">
        <RouterLink
          v-if="item.enabled && item.to"
          v-slot="{ isActive }"
          :to="item.to"
          class="sidebar__link"
          active-class="sidebar__link--active"
          @click="$emit('navigate')"
        >
          <PiIcon :name="item.icon" class="sidebar__link-icon" />
          <Text is="span" type="body" weight="medium" :color="isActive ? 'inverse' : undefined">
            {{ item.label }}
          </Text>
        </RouterLink>
        <Box
          v-else
          flex
          align="center"
          gap="3"
          class="sidebar__link sidebar__link--disabled"
          aria-disabled="true"
        >
          <PiIcon :name="item.icon" class="sidebar__link-icon" />
          <Text is="span" type="body" weight="medium">{{ item.label }}</Text>
          <Text is="span" type="caption-sm" color="muted" class="sidebar__soon">Soon</Text>
        </Box>
      </template>

      <Box class="sidebar__divider" />

      <Box
        v-for="item in supportItems"
        :key="item.label"
        flex
        align="center"
        gap="3"
        class="sidebar__link sidebar__link--disabled"
        aria-disabled="true"
      >
        <PiIcon :name="item.icon" class="sidebar__link-icon" />
        <Text is="span" type="body" weight="medium">{{ item.label }}</Text>
      </Box>
    </Box>

    <Box direction="col" gap="3" class="sidebar__footer">
      <Box flex align="center" gap="2" class="sidebar__vara">
        <Image :src="varaLogo" alt="" height="26" class="sidebar__vara-logo" />
        <Box direction="col">
          <Text is="span" type="caption" weight="semibold" color="default">VARA Compliant</Text>
          <Text is="span" type="caption-sm" color="muted">Virtual asset compliance</Text>
        </Box>
      </Box>
      <Text type="caption-sm" color="muted" align="center">Sliceal · v{{ appVersion }}</Text>
    </Box>
  </Box>
</template>

<style scoped>
.sidebar {
  width: 248px;
  flex-shrink: 0;
  height: 100%;
  background: var(--p-surface-0);
  border-right: 1px solid var(--p-content-border-color);
  padding: 1.25rem 0.875rem;
  box-sizing: border-box;
}

.sidebar__brand {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem 1.25rem;
  text-decoration: none;
}

.sidebar__brand-wordmark {
  height: 18px;
  width: auto;
}

.sidebar__nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
}

.sidebar__nav::-webkit-scrollbar {
  display: none;
}

.sidebar__link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  border-radius: var(--p-border-radius-md);
  color: var(--p-text-muted-color);
  text-decoration: none;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.sidebar__link:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.sidebar__link--active {
  background: var(--p-primary-600);
  color: var(--p-surface-0);
}

.sidebar__link--active:hover {
  background: var(--p-primary-600);
  color: var(--p-surface-0);
}

.sidebar__link-icon {
  font-size: 1rem;
}

.sidebar__link--disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.sidebar__link--disabled:hover {
  background: transparent;
  color: var(--p-text-muted-color);
}

.sidebar__soon {
  margin-left: auto;
}

.sidebar__divider {
  height: 1px;
  background: var(--p-content-border-color);
  margin: 0.75rem 0.5rem;
}

.sidebar__footer {
  padding-top: 1rem;
}

.sidebar__vara {
  padding: 0.75rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
}

.sidebar__vara-logo {
  flex-shrink: 0;
  width: auto;
}
</style>
